import { useCallback, useEffect, useRef, useState } from 'react';
import Beacon from '../Beacon.jsx';
import { useCompanyBilling } from '../CompanyBilling.jsx';
import { api } from '../../api.js';
import { hasNamedTenantAction } from '../../product-policy.js';
import { safeFailureMessage } from '../../safe-failure.js';
import {
  useConfirmationDialog,
} from '../ConfirmationDialog.jsx';
import {
  CAMPAIGN_PRIMARY_METRICS,
  CampaignMetricValue,
  CampaignStatePill,
} from './CampaignSummaryCard.jsx';
import { CampaignConfirmationDialog, CampaignModal } from './CampaignOverlays.jsx';
import { CampaignIcon } from './CampaignInventory.jsx';
import { Button } from '../primitives.jsx';

const ICON_BUTTON_STYLE = Object.freeze({ padding: 0, justifyContent: 'center' });

const rememberedAdFilters = new Map();
const SAFE_STATUS = /^[A-Z][A-Z0-9_]{0,63}$/;
const FRESHNESS_STATES = new Set(['available', 'partial', 'stale', 'no_data', 'unavailable']);
const headers = (companyId) => ({ headers: { 'X-Company-Id': companyId } });
const safeText = (value, maximum = 240) =>
  typeof value === 'string' && value.length <= maximum ? value : '';
const normalizedFreshness = (value) => ({
  state: FRESHNESS_STATES.has(value?.state) ? value.state : 'unavailable',
});

export function normalizeCampaignAds(response, campaignId, datePreset) {
  if (
    response?.campaignId !== campaignId ||
    response?.datePreset !== datePreset ||
    !Array.isArray(response.ads) ||
    response.ads.length > 100
  ) {
    throw Object.assign(new Error('Campaign ads response is invalid.'), {
      code: 'campaign_ads_response_invalid',
    });
  }
  const seen = new Set();
  const ads = response.ads.map((ad) => {
    const id = safeText(ad?.id, 128);
    if (!id || seen.has(id))
      throw Object.assign(new Error('Campaign ads response is invalid.'), {
        code: 'campaign_ads_response_invalid',
      });
    seen.add(id);
    return {
      id,
      name: safeText(ad.name) || 'Approved ad',
      renovationTypeId: safeText(ad.renovationTypeId, 128),
      renovationTypeName: safeText(ad.renovationTypeName) || 'Other renovation',
      configuredStatus: SAFE_STATUS.test(ad.configuredStatus || '')
        ? ad.configuredStatus
        : 'UNKNOWN',
      effectiveStatus: SAFE_STATUS.test(ad.effectiveStatus || '')
        ? ad.effectiveStatus
        : 'UNKNOWN',
      freshness: normalizedFreshness(ad.freshness),
      retryDescriptor:
        ad.retryDescriptor &&
        /^[A-Za-z0-9:_-]{16,200}$/.test(ad.retryDescriptor.token || '') &&
        ['pause', 'enable'].includes(ad.retryDescriptor.action) &&
        safeText(ad.retryDescriptor.operationId, 128)
          ? {
              token: ad.retryDescriptor.token,
              action: ad.retryDescriptor.action,
              operationId: ad.retryDescriptor.operationId,
            }
          : null,
      actions: Object.fromEntries(
        ['pause', 'enable'].map((action) => [
          action,
          { allowed: ad.actions?.[action]?.allowed === true },
        ]),
      ),
      metrics: Object.fromEntries(
        CAMPAIGN_PRIMARY_METRICS.map(({ key }) => {
          const metric = ad.metrics?.[key];
          const value =
            typeof metric?.value === 'number' &&
            Number.isFinite(metric.value) &&
            metric.value >= 0
              ? metric.value
              : null;
          return [
            key,
            {
              value,
              availability:
                metric?.availability === 'available' && value !== null
                  ? 'available'
                  : 'unavailable',
              unit: safeText(metric?.unit, 16),
            },
          ];
        }),
      ),
    };
  });
  return {
    ...response,
    ads,
    freshness: normalizedFreshness(response.freshness),
    error: response.error ? {
      code: /^[a-z][a-z0-9_]{0,95}$/.test(response.error.code || '')
        ? response.error.code : 'campaign_ads_provider_unavailable',
      retryable: response.error.retryable === true,
    } : null,
    currency: /^[A-Z]{3}$/.test(response.currency || '')
      ? response.currency
      : '',
    timezone: safeText(response.account?.timezone, 100),
  };
}

export function campaignAdsReportingState(response) {
  const states = [response?.freshness?.state, ...(response?.ads || []).map((ad) => ad.freshness?.state)];
  return {
    stale: states.includes('stale'),
    // The real ad endpoint uses freshness.state, not a top-level availability.
    partial: states.includes('partial')
      || (states.includes('available') && states.some((state) => ['no_data', 'unavailable'].includes(state))),
  };
}

export function groupCampaignAds(ads, filter = 'all') {
  const groups = new Map();
  for (const ad of ads) {
    const key = ad.renovationTypeId || ad.renovationTypeName;
    if (!groups.has(key))
      groups.set(key, { key, name: ad.renovationTypeName, ads: [] });
    if (filter === 'enabled' && ad.configuredStatus !== 'ACTIVE') continue;
    if (filter === 'paused' && ad.configuredStatus !== 'PAUSED') continue;
    groups.get(key).ads.push(ad);
  }
  return [...groups.values()];
}

export function adDeliveryLabel(ad) {
  if (ad.configuredStatus === 'PAUSED') return 'Paused individually';
  const labels = {
    ACTIVE: 'Enabled · Meta permits delivery',
    CAMPAIGN_PAUSED: 'Enabled · campaign paused',
    ADSET_PAUSED: 'Enabled · ad set paused',
    PENDING_REVIEW: 'Enabled · in review',
    DISAPPROVED: 'Not delivering · disapproved',
    WITH_ISSUES: 'Delivery needs attention',
    UNKNOWN: 'Delivery unverified',
    NOT_PUBLISHED: 'Not published',
  };
  return (
    labels[ad.effectiveStatus] ||
    `Effective status: ${ad.effectiveStatus.toLowerCase().replaceAll('_', ' ')}`
  );
}

export function CampaignAds({
  companyId,
  campaign,
  datePreset,
  productPolicy,
  refreshNonce = 0,
  onChanged,
  approvedVersions = [],
}) {
  const billing = useCompanyBilling();
  const scope = JSON.stringify([companyId, campaign.id, datePreset]);
  const [state, setState] = useState({
    scope: '',
    status: 'loading',
    response: null,
  });
  const filterScope = JSON.stringify([companyId, campaign.id]);
  const [savedFilter, setSavedFilter] = useState({ scope: filterScope, value: rememberedAdFilters.get(filterScope) || 'all' });
  const filter = savedFilter.scope === filterScope ? savedFilter.value : rememberedAdFilters.get(filterScope) || 'all';
  const setFilter = (value) => { rememberedAdFilters.set(filterScope, value); setSavedFilter({ scope: filterScope, value }); };
  const [reviewedCreative, setReviewedCreative] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [busy, setBusy] = useState('');
  const current = useRef(scope);
  current.current = scope;
  const version = useRef(0);
  const inFlight = useRef(false);
  const retryKeys = useRef(new Map());
  const confirmation = useConfirmationDialog();
  const load = useCallback(
    async ({ signal, preserve = false } = {}) => {
      const request = ++version.current;
      setState((previous) => ({
        scope,
        status: 'loading',
        response:
          preserve && previous.scope === scope ? previous.response : null,
      }));
      try {
        const response = normalizeCampaignAds(
          await api.get(
            `/api/saas/campaigns/${encodeURIComponent(campaign.id)}/ads?datePreset=${datePreset}`,
            { ...headers(companyId), signal },
          ),
          campaign.id,
          datePreset,
        );
        if (
          current.current !== scope ||
          request !== version.current ||
          signal?.aborted
        )
          return null;
        setState({ scope, status: 'ready', response });
        return response;
      } catch (error) {
        if (
          current.current !== scope ||
          request !== version.current ||
          signal?.aborted
        )
          return null;
        setState((previous) => ({
          ...previous,
          scope,
          status: 'error',
          error,
        }));
        return null;
      }
    },
    [scope],
  );
  useEffect(() => {
    const controller = new AbortController();
    load({ signal: controller.signal });
    return () => {
      controller.abort();
      version.current += 1;
    };
  }, [load, refreshNonce]);
  useEffect(
    () => () => {
      current.current = '';
    },
    [],
  );
  const response = state.scope === scope ? state.response : null;
  const reportingState = campaignAdsReportingState(response);
  const groups = groupCampaignAds(response?.ads || [], filter);
  const canManage =
    hasNamedTenantAction(productPolicy, 'campaign.pause') &&
    hasNamedTenantAction(productPolicy, 'campaign.resume');
  async function toggle(ad) {
    const action =
      ad.retryDescriptor?.action ||
      (ad.configuredStatus === 'ACTIVE' ? 'pause' : 'enable');
    if (
      inFlight.current ||
      state.status !== 'ready' ||
      campaign.archivedAt ||
      !canManage ||
      !ad.actions[action].allowed
    )
      return;
    if (action === 'enable' && !await billing.requestAccess('resume')) return;
    const approved = await confirmation.request({
      title: action === 'pause' ? 'Pause this ad?' : 'Enable this ad?',
      description:
        action === 'pause'
          ? `“${ad.name}” will stop delivering. Other enabled ads share the campaign budget and can continue. Performance history is preserved.`
          : `“${ad.name}” will be enabled within the shared campaign budget. It stays stopped while its parent campaign is paused. If the campaign is active, delivery and spending may begin once Meta permits delivery.`,
      confirmLabel: action === 'pause' ? 'Pause ad' : 'Enable ad',
      tone: 'warning',
    });
    if (!approved || current.current !== scope || inFlight.current) return;
    inFlight.current = true;
    setBusy(ad.id);
    setFeedback({
      status: 'pending',
      message: 'Waiting for Meta to confirm the ad setting…',
    });
    const key = `${scope}:${ad.id}:${action}`;
    if (ad.retryDescriptor?.action === action)
      retryKeys.current.set(key, ad.retryDescriptor.token);
    if (!retryKeys.current.has(key))
      retryKeys.current.set(
        key,
        `campaign-ad:${action}:${globalThis.crypto.randomUUID()}`,
      );
    try {
      const result = await api.post(
        `/api/saas/campaigns/${encodeURIComponent(campaign.id)}/ads/${encodeURIComponent(ad.id)}/operations/${action}`,
        { confirmed: true, idempotencyKey: retryKeys.current.get(key) },
        headers(companyId),
      );
      const refreshed = await load({ preserve: true });
      if (current.current !== scope) return;
      const confirmed =
        result?.operation?.type === 'campaign_ad_status_update' &&
        ['completed', 'succeeded'].includes(result.operation.status) &&
        result.operation.reconciliationStatus === 'confirmed' &&
        refreshed?.ads.find((candidate) => candidate.id === ad.id)
          ?.configuredStatus === (action === 'pause' ? 'PAUSED' : 'ACTIVE');
      if (confirmed) retryKeys.current.delete(key);
      setFeedback({
        status: confirmed ? 'success' : 'warning',
        message: confirmed
          ? action === 'pause'
            ? 'Ad paused. Its performance history is preserved.'
            : 'Ad enabled. Parent status and Meta review still determine delivery.'
          : 'The request was accepted, but the ad setting is not yet confirmed. Refresh or retry; the same request will be reused.',
      });
      await onChanged?.();
    } catch (error) {
      if (error?.code === 'billing_subscription_required' && current.current === scope) await billing.refresh();
      if (current.current === scope)
        setFeedback({
          status: 'error',
          message: safeFailureMessage(error, 'curated-campaign'),
        });
    } finally {
      inFlight.current = false;
      if (current.current === scope) setBusy('');
    }
  }
  return (
    <div className="bl-campaign-expanded-ads">
      <div className="bl-campaign-ads-heading">
        <div>
          <h4>Ads, by renovation type</h4>
          <p>
            {datePreset === 'today' ? 'Today' : datePreset === 'last_7d' ? 'Last 7 days' : datePreset === 'maximum' ? 'Lifetime' : 'Last 30 days'} · Meta-reported performance
          </p>
        </div>
        <div role="group" aria-label="Ad status filter">
          {[
            ['all', 'All ads'],
            ['enabled', 'Enabled'],
            ['paused', 'Paused'],
          ].map(([value, label]) => (
            <button
              type="button"
              key={value}
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {state.status === 'loading' && !response && (
        <p><Beacon size="control" label="Checking Meta performance for the approved ads…" delayMs={0} /></p>
      )}
      {(state.status === 'error' || response?.error) && (
        <div role="alert">
          <p>Some ad delivery or performance information could not be verified. {safeFailureMessage(state.status === 'error' ? state.error : response.error, 'curated-campaign')}</p>
          <button type="button" disabled={state.status === 'loading' || Boolean(busy)} onClick={() => load({ preserve: true })}>
            Retry ads
          </button>
        </div>
      )}
      {feedback?.message && (
        <p
          className={`bl-campaign-inline-feedback is-${feedback.status}`}
          role={feedback.status === 'error' ? 'alert' : 'status'}
        >
          {feedback.message}
        </p>
      )}
      {response?.hierarchy?.configured?.zeroEnabledAds && <p className="bl-campaign-inline-feedback is-warning" role="status">No ads are enabled. Enable an ad before resuming delivery.</p>}
      {response && (
        <>
          {!groups.length && (
            <p>
              No {filter === 'all' ? '' : `${filter} `}ads in this campaign.
            </p>
          )}
          {groups.map((group) => (
            <section className="bl-campaign-ad-group" key={group.key}>
              <h5>
                {group.name}<span className="bl-campaign-ad-group-rule" />
                <small>{group.ads.length} {group.ads.length === 1 ? 'ad' : 'ads'} · shared campaign budget</small>
              </h5>
              {group.ads.length ? <div className="bl-campaign-ad-table">
                <div className="bl-campaign-ad-table-header" aria-hidden="true"><span>Creative</span><span>Status</span><span>Leads</span><span>Cost / lead</span><span>Spend</span><span>CTR</span><span /></div>
              {group.ads.map((ad) => {
                const approved = approvedVersions.find((version) => version.creativeVersionId === ad.id);
                const delivery = ad.configuredStatus === 'PAUSED' ? 'Paused' : ({ ACTIVE: 'Active', CAMPAIGN_PAUSED: 'Campaign paused', ADSET_PAUSED: 'Ad set paused', PENDING_REVIEW: 'In review', DISAPPROVED: 'Disapproved', WITH_ISSUES: 'Check delivery', UNKNOWN: 'Unverified', NOT_PUBLISHED: 'Not published' }[ad.effectiveStatus] || 'Check delivery');
                const copy = <><span className="bl-campaign-ad-thumb"><CampaignIcon name="video" size={16} /></span><span><strong>{ad.name}</strong><small>{approved?.packageName || 'Approved creative'}</small></span></>;
                return <div className="bl-campaign-ad-row" key={ad.id}>
                  {approved ? <button type="button" className="bl-campaign-ad-copy bl-campaign-creative-details" aria-label={`Review creative ${ad.name}`} onClick={() => setReviewedCreative({ ...approved, name: ad.name, scope })}>{copy}</button> : <div className="bl-campaign-ad-copy">{copy}</div>}
                  <div className="bl-campaign-ad-delivery" title={adDeliveryLabel(ad)}><CampaignStatePill tone={delivery === 'Active' ? 'live' : delivery === 'Paused' ? 'warn' : 'neutral'} className={`bl-campaign-state ${delivery === 'Active' ? 'is-active' : ''}`} aria-label={adDeliveryLabel(ad)}>{delivery}</CampaignStatePill></div>
                  <div className="bl-campaign-row-metrics">
                    {CAMPAIGN_PRIMARY_METRICS.map(({ key, label, kind }) => (
                      <div key={key} className="bl-campaign-row-metric">
                        <span>{label}</span>
                        <strong aria-label={ad.metrics[key]?.availability !== 'available' || (kind === 'money' && !response.currency) ? `${label}: unavailable` : undefined}>
                          {kind === 'money' && !response.currency ? '—' : <CampaignMetricValue metric={ad.metrics[key]} kind={kind} currency={response.currency} unavailable="—" />}
                        </strong>
                      </div>
                    ))}
                  </div>
                  {!campaign.archivedAt && canManage && (
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      className="bl-campaign-icon-button"
                      style={ICON_BUTTON_STYLE}
                      aria-label={`${ad.retryDescriptor ? 'Retry ' : ''}${(ad.retryDescriptor?.action || (ad.configuredStatus === 'ACTIVE' ? 'pause' : 'enable')) === 'pause' ? 'Pause' : 'Enable'} ad ${ad.name}`}
                      title={
                        ad.retryDescriptor
                          ? 'Reconcile the previous ad request'
                          : ad.configuredStatus === 'ACTIVE'
                            ? 'Pause ad'
                            : 'Enable ad'
                      }
                      disabled={
                        Boolean(busy) ||
                        ((ad.retryDescriptor?.action || (ad.configuredStatus === 'ACTIVE' ? 'pause' : 'enable')) === 'enable' && !billing.canResume) ||
                        state.status !== 'ready' ||
                        !ad.actions[
                          ad.retryDescriptor?.action ||
                            (ad.configuredStatus === 'ACTIVE'
                              ? 'pause'
                              : 'enable')
                        ].allowed
                      }
                      onClick={() => toggle(ad)}
                    >
                      <CampaignIcon
                        name={
                          ad.configuredStatus === 'ACTIVE' ? 'pause' : 'resume'
                        }
                      />
                    </Button>
                  )}
                </div>
              })}
              </div> : <p className="bl-campaign-ad-panel-note">No ads match this filter in this group.</p>}
            </section>
          ))}
          <p className="bl-campaign-ads-provenance">
            {state.status === 'error'
              ? 'Refresh failed · showing last loaded metrics · '
              : state.status === 'loading'
                ? <><Beacon size="inline" tone="neutral" delayMs={0} slowAfterMs={null} /> Refreshing · </>
                : ''}
            Meta Insights · {response.currency || 'Currency unavailable'}
            {response.timezone ? ` · ${response.timezone}` : ''} ·{' '}
            {datePreset === 'maximum'
              ? 'Lifetime'
              : datePreset === 'today'
                ? 'Today'
                : datePreset === 'last_7d'
                  ? 'Last 7 days'
                  : 'Last 30 days'}
            {reportingState.stale ? ' · Stale metrics' : ''}
            {reportingState.partial ? ' · Partial metrics' : ''}.
            Unavailable results show —.
          </p>

        </>
      )}
      <p className="bl-campaign-ad-panel-note">{campaign.status === 'paused'
        ? 'Enabled ads will stay stopped until the campaign resumes. Individually paused ads remain paused.'
        : ['draft', 'awaiting_approval', 'approved'].includes(campaign.status)
          ? 'These creatives are saved with the campaign. Performance and delivery controls appear after publication.'
          : 'Pausing an ad keeps its performance history. The remaining enabled ads share the campaign budget.'}</p>
      <CampaignConfirmationDialog {...confirmation.dialogProps} icon={<CampaignIcon name={confirmation.dialogProps.confirmLabel === 'Pause ad' ? 'pause' : 'resume'} size={20} />} />
      {reviewedCreative?.scope === scope && <CampaignModal title={reviewedCreative.name} description="Exact approved creative" onClose={() => setReviewedCreative(null)}
        footer={<button type="button" data-campaign-safe-focus onClick={() => setReviewedCreative(null)}>Close</button>}>
        <dl className="bl-campaign-settings-facts">{[
          ['Creative version', reviewedCreative.creativeVersionId], ['Renovation type', reviewedCreative.renovationTypeName || reviewedCreative.renovationTypeKey],
          ['Package', reviewedCreative.packageName], ['Primary text', reviewedCreative.primaryText], ['Headline', reviewedCreative.headline], ['Call to action', reviewedCreative.callToAction],
        ].map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value || 'Not supplied'}</dd></div>)}</dl>
      </CampaignModal>}
    </div>
  );
}
